import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CatalogItem, DraftCatalog } from '@/lib/draft-order-client';
import { ItemPicker } from './item-picker';
import { QuantityStepper } from './quantity-stepper';
import { lineSubtotal, type DraftLine } from './cart-draft';

interface CartItemsSectionProps {
  lines: DraftLine[];
  catalog?: DraftCatalog;
  currencySymbol: string;
  editable: boolean;
  onAdd: (item: CatalogItem) => void;
  onEdit: (line: DraftLine) => void;
  onRemove: (key: string) => void;
  onQuantityChange: (key: string, quantity: number) => void;
}

export function CartItemsSection({
  lines,
  catalog,
  currencySymbol,
  editable,
  onAdd,
  onEdit,
  onRemove,
  onQuantityChange,
}: CartItemsSectionProps) {
  const { t } = useTranslation('conversations');

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('cart.items.title')}</h3>

      {lines.length === 0 && <p className="text-sm text-muted-foreground">{t('cart.items.empty')}</p>}

      <ul className="space-y-3">
        {lines.map((line) => (
          <li key={line.key} className="space-y-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-foreground">
                  <span className="tabular-nums">{line.quantity}x</span> {line.name}
                </p>

                {line.options.length > 0 && (
                  <ul className="ml-5 space-y-0.5 text-xs text-muted-foreground">
                    {groupByName(line).map(([groupName, options]) => (
                      <li key={groupName}>
                        <span className="font-medium">{groupName}: </span>
                        {options
                          .map(
                            (option) =>
                              `${option.optionName}${option.priceDelta ? ` (${option.priceDelta > 0 ? '+' : ''}${option.priceDelta.toFixed(2)})` : ''}`,
                          )
                          .join(', ')}
                      </li>
                    ))}
                  </ul>
                )}

                {line.comments && <p className="ml-5 text-xs italic text-muted-foreground">“{line.comments}”</p>}

                {(line.orphaned || line.available === false) && (
                  <p className="ml-5 mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                    <TriangleAlert className="h-3 w-3 shrink-0" />
                    {line.orphaned ? t('cart.items.orphaned') : t('cart.items.unavailableLine')}
                  </p>
                )}

                {/* The line is still priced at what the customer agreed to; saving is what moves it. */}
                {line.newUnitPrice !== undefined && (
                  <p className="ml-5 mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                    <TriangleAlert className="h-3 w-3 shrink-0" />
                    {t('cart.items.repriced', {
                      from: `${currencySymbol}${line.unitPrice.toFixed(2)}`,
                      to: `${currencySymbol}${line.newUnitPrice.toFixed(2)}`,
                    })}
                  </p>
                )}
              </div>

              <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
                {currencySymbol}
                {lineSubtotal(line).toFixed(2)}
              </span>
            </div>

            {editable && (
              <div className="ml-5 flex items-center gap-1">
                <QuantityStepper
                  value={line.quantity}
                  onChange={(quantity) => onQuantityChange(line.key, quantity)}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  // An orphaned line has no catalog entry left to configure against — it can only go.
                  disabled={line.orphaned}
                  onClick={() => onEdit(line)}
                  aria-label={t('cart.items.edit')}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onRemove(line.key)}
                  aria-label={t('cart.items.remove')}
                  className={cn('text-destructive hover:text-destructive')}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {editable && <ItemPicker catalog={catalog} currencySymbol={currencySymbol} onSelect={onAdd} />}
    </section>
  );
}

function groupByName(line: DraftLine): [string, DraftLine['options']][] {
  const grouped = line.options.reduce<Record<string, DraftLine['options']>>((acc, option) => {
    (acc[option.groupName] ??= []).push(option);
    return acc;
  }, {});
  return Object.entries(grouped);
}
